<script lang="ts" setup>
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router/composables'
import { settingCommonConfig } from './configs/common'
import SettingPageDefault from './components/SettingPage.default.vue'
import SettingPageFlipnovel from './components/SettingPage.flipnovel.vue'
import SettingPageNovelyn from './components/SettingPage.novelyn.vue'
import SettingPageNovelmello from './components/SettingPage.novelmello.vue'
import { api } from '@/api'
import { SUBSCRIBE_PAY_TYPE } from '@/constants'
import { brand } from '@/config/brands'
import { useToastStore, useUserStore } from '@/stores'
import { pageViewTrack, useElementClick } from '@/composables/tracks'
import { LOCALE_MAP, type LocaleCode, i18n, setLocale } from '@/i18n'
import { loadBrandComponent } from '@/utils/loadBrandConfig'

const config = settingCommonConfig
const isFlipnovelFamily = ['flipnovel', 'storlyn', 'moonpage', 'novaris', 'novelune'].includes(brand)

const SettingPage = loadBrandComponent({
  rocnovel: SettingPageDefault,
  oceread: SettingPageDefault,
  talewave: SettingPageDefault,
  ficmoon: SettingPageDefault,
  novelarc: SettingPageDefault,
  storyella: SettingPageDefault,
  aurenovel: SettingPageDefault,
  silktale: SettingPageDefault,
  pearla: SettingPageDefault,
  fictora: SettingPageDefault,
  marinovel: SettingPageDefault,
  flipnovel: SettingPageFlipnovel,
  moonpage: SettingPageFlipnovel,
  novaris: SettingPageFlipnovel,
  novelune: SettingPageFlipnovel,
  novelyn: SettingPageNovelyn,
  novelmello: SettingPageNovelmello,
})

const { $toast } = useToastStore()
const router = useRouter()

// 从环境变量获取链接
const userAgreementUrl = import.meta.env.VITE_USER_AGREEMENT_URL
const privacyPolicyUrl = import.meta.env.VITE_PRIVACY_POLICY_URL
// 订阅管理(一期): 跳转 PayPal 官方「取消自动扣款」帮助页
const subscriptionManagementUrl = 'https://www.paypal.com/us/cshelp/article/what-is-an-automatic-payment-and-how-do-i-update-or-cancel-one-help240'

const userStore = useUserStore()
const { elementClick } = useElementClick()

// 订阅管理入口:仅 PayPal 渠道(payType=6)的自动续费订阅展示;Stripe(payType=5)不展示
const showSubscriptionManagement = computed(() =>
  !!userStore.$state.subscribeRenewal && userStore.$state.payType === SUBSCRIBE_PAY_TYPE.PAYPAL,
)

// 语言切换
const currentLocale = ref<LocaleCode>(i18n.locale as LocaleCode)
const languageModalVisible = ref(false)
const localeLabelMap: Record<LocaleCode, () => string> = {
  'en': () => config.texts.languageEnglish,
  'zh-TW': () => config.texts.languageTraditionalChinese,
  'ja': () => config.texts.languageJapanese,
  'ko': () => config.texts.languageKorean,
  'fr': () => config.texts.languageFrench,
  'de': () => config.texts.languageGerman,
  'es': () => config.texts.languageSpanish,
  'id': () => config.texts.languageBahasaIndonesia,
}
const localeOptions = Object.keys(localeLabelMap) as LocaleCode[]
function getLocaleLabel(locale: LocaleCode) {
  const getter = localeLabelMap[locale]
  return getter ? getter() : locale
}
const currentLocaleLabel = computed(() => {
  return getLocaleLabel(currentLocale.value)
})
function onClickLanguage() {
  languageModalVisible.value = true
}
function onSelectLanguage(locale: LocaleCode) {
  if (locale === currentLocale.value)
    return
  elementClick(`setting-language-${locale}`)
  setLocale(locale)
  currentLocale.value = locale
  languageModalVisible.value = false
  api.post(`/member/setting/language/set?language=${LOCALE_MAP[locale]}`).catch((error: any) => {
    console.error(error)
  })
}

// 注销账户
const unsubscribeVisible = ref(false)
const unsubscribeLoading = ref(false)
function closeUnsubscribeDialog() {
  unsubscribeVisible.value = false
}

async function onUnsubscribe() {
  if (unsubscribeLoading.value)
    return
  unsubscribeLoading.value = true
  try {
    await api.post('/member/sign/out')
    $toast(config.texts.accountCancellationSuccess)
    unsubscribeVisible.value = false
    unsubscribeLoading.value = false
    localStorage.clear()
    sessionStorage.clear()
    router.replace({ name: isFlipnovelFamily ? 'home' : 'member' })
  }
  catch (error: any) {
    console.error(error)
  }
}

function showLogOffDialog() {
  unsubscribeVisible.value = true
  pageViewTrack('account_Agreement')
}

// 自动解锁
const autoLockStatus = ref(userStore.$state.autoLock)
const autoLockLoading = ref(false)
async function onAutoLock() {
  if (autoLockLoading.value)
    return
  autoLockLoading.value = true

  elementClick(!autoLockStatus.value ? 'auto-unlock-on' : 'auto-unlock-off')
  try {
    await api.post('/member/update/auto-lock', {
      autoLock: !autoLockStatus.value,
    })
    autoLockStatus.value = !autoLockStatus.value
    $toast(config.texts.autoUnlockSuccess)
  }
  catch (error: any) {
    console.error(error)
  }
  finally {
    autoLockLoading.value = false
  }
}
</script>

<template>
  <SettingPage
    :config="config"
    :current-locale-label="currentLocaleLabel"
    :current-locale="currentLocale"
    :locale-options="localeOptions"
    :language-modal-visible="languageModalVisible"
    :unsubscribe-visible="unsubscribeVisible"
    :auto-lock-status="autoLockStatus"
    :show-subscription-management="showSubscriptionManagement"
    :user-agreement-url="userAgreementUrl"
    :privacy-policy-url="privacyPolicyUrl"
    :subscription-management-url="subscriptionManagementUrl"
    :on-click-language="onClickLanguage"
    :get-locale-label="getLocaleLabel"
    :on-select-language="onSelectLanguage"
    :show-log-off-dialog="showLogOffDialog"
    :on-close-unsubscribe="closeUnsubscribeDialog"
    :on-auto-lock="onAutoLock"
    :on-unsubscribe="onUnsubscribe"
    @update:language-modal-visible="languageModalVisible = $event"
    @update:unsubscribe-visible="unsubscribeVisible = $event"
  />
</template>
